<div class="container mx-auto px-4" [class.dark]="themeService.isDark()">

    <!-- Calculator Form -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

      <!-- Input Section -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-6">Input Parameters</h2>

        <form [formGroup]="bigUpYieldForm" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Left Column -->
            <div class="space-y-4">
              <!-- Cash From Operations -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cash From Operations
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    formControlName="cashFromOperations"
                    placeholder="Enter cash from operations"
                    step="0.01"
                    min="0"
                    class="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Operating cash flow from the cash flow statement</small>
              </div>

              <!-- Capital Expenditures -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Capital Expenditures (CapEx)
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    formControlName="capitalExpenditures"
                    placeholder="Enter capital expenditures"
                    step="0.01"
                    min="0"
                    class="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    (input)="updateMaintenanceCapEx()"
                  />
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Total capital expenditures for the period</small>
              </div>

              <!-- Maintenance CapEx Percentage -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  % for Maintenance
                </label>
                <div class="relative">
                  <input
                    type="number"
                    formControlName="maintenanceCapExPercent"
                    placeholder="Enter maintenance percentage"
                    step="0.01"
                    min="0"
                    max="100"
                    class="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    (input)="updateMaintenanceCapEx()"
                  />
                  <span class="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">%</span>
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">% of CapEx used for maintenance (default: 70%)</small>
              </div>

              <!-- Maintenance CapEx (Calculated but Editable) -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Maintenance CapEx
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    formControlName="maintenanceCapEx"
                    placeholder="Calculated maintenance CapEx"
                    step="0.01"
                    min="0"
                    class="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Calculated as CapEx × % for Maintenance</small>
              </div>

            </div>

            <!-- Right Column -->
            <div class="space-y-4">

              <!-- Tax Provision -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tax Provision
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    formControlName="taxProvision"
                    placeholder="Enter tax provision"
                    step="0.01"
                    min="0"
                    class="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Income tax provision from the income statement</small>
              </div>

              <!-- Owner's Earnings (Calculated Read-Only) -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Owner's Earnings (Calculated)
                </label>
                <div class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
                  ${{ calculatedOwnerEarnings | number:'1.2-2' }}
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Oper Cash - Maintenance CapEx - Tax Provision</small>
              </div>

              <!-- Shares Outstanding -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Shares Outstanding
                </label>
                <div class="relative">
                  <input
                    type="number"
                    formControlName="sharesOutstanding"
                    placeholder="Enter shares outstanding"
                    step="1"
                    min="0"
                    class="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Total shares outstanding (in millions)</small>
              </div>

              <!-- Owner's Earnings Per Share (Calculated Read-Only) -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Owner's Earnings Per Share (Calculated)
                </label>
                <div class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
                  ${{ calculatedOwnerEarningsPerShare | number:'1.2-2' }}
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Owner's Earnings ÷ Shares Outstanding</small>
              </div>

            </div>

          </div>

          <!-- Calculate Button -->
          <div class="pt-4">
            <button
              type="button"
              class="w-full text-white bg-orange-700 hover:bg-orange-800 focus:ring-4 focus:ring-orange-300 font-medium rounded-lg text-lg px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              [disabled]="bigUpYieldForm.invalid || isLoading()"
              (click)="calculateBigUpYield()">
              {{ isLoading() ? 'Calculating...' : 'Calculate Yield' }}
            </button>
          </div>

          <!-- Clear Button -->
          <div>
            <button
              type="button"
              class="w-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-lg px-5 py-3 flex items-center justify-center"
              (click)="clearForm()">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Clear All
            </button>
          </div>

        </form>
      </div>

      <!-- Results Section -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-6">Calculation Results</h2>

        <!-- Results Display -->
        <div *ngIf="isCalculated; else noResults">

          <!-- Buy Price Result -->
          <div class="calculation-result rounded-lg p-6 mb-6 text-center">
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">Buy Price</h3>
            <div class="result-highlight">${{ results.buyPrice | number:'1.2-2' }}</div>
            <p class="text-gray-600 dark:text-gray-400 mt-2">Maximum price to pay for 10% earnings yield</p>
          </div>

          <!-- Sticker Price Result -->
          <div class="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg p-6 mb-6 text-center border border-orange-200 dark:border-orange-800">
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">Sticker Price</h3>
            <div class="text-3xl font-bold text-orange-600 dark:text-orange-400">${{ results.stickerPrice | number:'1.2-2' }}</div>
            <p class="text-gray-600 dark:text-gray-400 mt-2">Fair value based on owner's earnings analysis</p>
          </div>

          <!-- Calculation Details -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
            <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4">Calculation Details</h3>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Cash From Operations:</span>
                <span class="font-medium text-gray-900 dark:text-white">${{ bigUpYieldForm.get('cashFromOperations')?.value | number:'1.2-2' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Maintenance CapEx:</span>
                <span class="font-medium text-gray-900 dark:text-white">${{ bigUpYieldForm.get('maintenanceCapEx')?.value | number:'1.2-2' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Tax Provision:</span>
                <span class="font-medium text-gray-900 dark:text-white">${{ bigUpYieldForm.get('taxProvision')?.value | number:'1.2-2' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Owner's Earnings:</span>
                <span class="font-medium text-gray-900 dark:text-white">${{ calculatedOwnerEarnings | number:'1.2-2' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Shares Outstanding:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ bigUpYieldForm.get('sharesOutstanding')?.value | number:'1.2-2' }}M</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Owner's Earnings Per Share:</span>
                <span class="font-medium text-gray-900 dark:text-white">${{ calculatedOwnerEarningsPerShare | number:'1.2-2' }}</span>
              </div>
            </div>
          </div>

          <!-- Investment Analysis -->
          <div class="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-6 border border-yellow-200 dark:border-yellow-800">
            <h3 class="text-lg font-semibold text-orange-800 dark:text-orange-300 mb-3">
              <i class="pi pi-chart-line mr-2"></i>Earnings Yield Analysis
            </h3>
            <p class="text-gray-700 dark:text-gray-300 leading-relaxed">{{ results.analysis }}</p>
          </div>

        </div>

        <!-- No Results Template -->
        <ng-template #noResults>
          <div class="text-center py-12">
            <i class="pi pi-chart-bar text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
            <p class="text-gray-500 dark:text-gray-400 text-lg">Enter parameters and click "Calculate Big Up Yield" to see results</p>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Information Section -->
    <div class="mt-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
      <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-6">About Big Up Yield Calculator</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3">Owner's Earnings Analysis</h3>
          <p class="text-gray-600 dark:text-gray-400 mb-4">
            This calculator determines the fair price of a stock based on Warren Buffett's concept of "owner's earnings" -
            the true cash flow available to shareholders after necessary maintenance capital expenditures and a minimum 10% yield
          </p>
          <div class="bg-gray-50 dark:bg-gray-700 rounded-md p-4">
            <h4 class="font-semibold text-gray-800 dark:text-white mb-2">Formula Used for Owners Earnings:</h4>
            <p class="text-gray-600 dark:text-gray-400 text-sm font-mono mb-2">
              Owner's Earnings = Cash From Operations - Maintenance CapEx - Tax Provision
            </p>
          </div>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3">Great For</h3>
          <ul class="text-gray-600 dark:text-gray-400 space-y-2">
            <li class="flex items-start">
              <i class="pi pi-check text-orange-500 mr-2 mt-1"></i>
              <span><strong>Value Stocks:</strong> Undervalued companies with strong earnings yields</span>
            </li>
            <li class="flex items-start">
              <i class="pi pi-check text-orange-500 mr-2 mt-1"></i>
              <span><strong>Mature Companies:</strong> Established businesses with predictable earnings</span>
            </li>
            <li class="flex items-start">
              <i class="pi pi-check text-orange-500 mr-2 mt-1"></i>
              <span><strong>Industrial Stocks:</strong> Manufacturing companies with significant CapEx</span>
            </li>
            <li class="flex items-start">
              <i class="pi pi-check text-orange-500 mr-2 mt-1"></i>
              <span><strong>Dividend Stocks:</strong> Companies that have great management and compounds earnings well.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
</div>

